import type { StoreDiagnosticCode } from "@intrasync/stores";
import { isSessionEnded } from "../stores/sessionGuard";

/**
 * The shared way a ViewStore reports a FAILED USER-INITIATED ACTION.
 *
 * THE PROBLEM IT REPLACES: across `src/views/**` there are ~173 `console.error`
 * calls inside catch blocks, and a large share of them are on delete/save
 * paths. The pattern is always the same —
 *
 *     try { await this.service?.remove(id); await this.reload(); }
 *     catch (err) { console.error("Failed to remove source:", err); }
 *
 * — which means the user clicked Delete, the row is still there, and the app
 * said NOTHING. They try again. Meanwhile the failure is in a console nobody
 * has open, with no store name, no stable code, and gone on the next reload.
 *
 * One call does all three things that failure needs:
 *   1. tells the USER, through the toast channel the app already renders
 *      (`rootStore.notificationStore` -> @intrasync/react's
 *      `NotificationProvider`, which is notistack — see the note below);
 *   2. records a STRUCTURED, REDACTED `view.action_failed` diagnostic through
 *      the library sink, so it lands in HQ's retained ring buffer and shows up
 *      in the admin diagnostics panel with the store name and a stable code;
 *   3. returns the user-facing message, so a store that also renders inline
 *      error text can assign it without composing the string twice.
 *
 * ON NOTISTACK: `notistack` is a direct dependency of `@intrasync/web` with no
 * import anywhere in `src/`. It is NOT unused — `@intrasync/react` depends on
 * it and its `NotificationProvider` (already mounted in `renderApp.tsx`) is the
 * SnackbarProvider. Importing notistack here and rendering a second provider
 * would give HQ two snackbar stacks and duplicate toasts. The correct use of
 * notistack in this app is `notificationStore.addErrorNotification`, which is
 * what this helper calls.
 *
 * ON VOLUME: this is for user-initiated actions only. Library-level info/warn
 * is for the admin panel and the console, never a toast — a socket that flaps
 * would otherwise carpet the screen.
 */

/** The slice of the RootStore this helper needs. Structural on purpose: view
 *  stores are constructed with HQ's RootStore, tests pass a two-field stub. */
export interface ActionFailureHost {
  diagnostics?: {
    error(
      store: string,
      code: StoreDiagnosticCode,
      message: string,
      detail?: { error?: unknown; data?: Record<string, unknown> },
    ): void;
  };
  notificationStore?: {
    addErrorNotification(message: string): string;
  } | null;
}

export interface ActionFailureOptions {
  /** Emitting store, e.g. "MeetingSourcesStore". Shown in the panel. */
  store: string;
  /** What the user was trying to do, phrased as a verb phrase:
   *  "remove the meeting source". Used to build the default message. */
  action: string;
  error: unknown;
  /** Overrides the derived message entirely. */
  userMessage?: string;
  /** Structured context for the diagnostic (redacted before delivery). */
  data?: Record<string, unknown>;
  /** Defaults to `view.action_failed`. Must stay inside the library's union —
   *  a code is public API the moment anyone alerts on it. */
  code?: StoreDiagnosticCode;
  /** Set false to record the diagnostic without toasting (background retries). */
  notify?: boolean;
}

interface ErrorLike {
  message?: unknown;
  code?: unknown;
  name?: unknown;
}

/** HTTP-ish codes worth a specific sentence. Anything else falls through to
 *  the server's own message, which Feathers errors carry and which is usually
 *  the most useful thing available. */
const CODE_MESSAGES: Record<number, string> = {
  401: "Your session has expired. Sign in again and retry.",
  403: "You do not have permission to do that.",
  404: "That record no longer exists — it may have been deleted already.",
  408: "The server took too long to respond. Try again.",
  409: "Someone else changed that record. Reload and try again.",
  429: "Too many requests. Wait a moment and try again.",
};

/** A message that tells the user nothing, so it should not be shown to them. */
function isUselessMessage(message: string): boolean {
  const trimmed = message.trim();
  if (trimmed.length === 0) return true;
  return /^(error|failed|request failed|network error|internal server error)$/i.test(
    trimmed,
  );
}

/** Build the sentence the user sees. Exported for tests and for callers that
 *  want the text without the side effects. */
export function describeActionFailure(action: string, error: unknown): string {
  const err = (error ?? {}) as ErrorLike;
  const code = typeof err.code === "number" ? err.code : undefined;
  if (code !== undefined && CODE_MESSAGES[code]) {
    return `Could not ${action}. ${CODE_MESSAGES[code]}`;
  }
  if (code !== undefined && code >= 500) {
    return `Could not ${action}. The server reported an error — try again, and tell an administrator if it keeps happening.`;
  }
  const message = typeof err.message === "string" ? err.message : "";
  if (message && !isUselessMessage(message)) {
    return `Could not ${action}: ${message}`;
  }
  return `Could not ${action}. Please try again.`;
}

/**
 * The host, or a thunk that produces it.
 *
 * The thunk form is not decoration: a ViewStore's `rootStore` is assigned in
 * the CONSTRUCTOR BODY, while a `private report = createActionReporter(...)`
 * field initializer runs BEFORE that body — so binding the host eagerly in a
 * field captures `undefined`. `createActionReporter(() => this.rootStore, …)`
 * is the form that works in a field, and it is the form the examples use.
 */
export type ActionFailureHostSource =
  | ActionFailureHost
  | null
  | undefined
  | (() => ActionFailureHost | null | undefined);

function resolveHost(
  source: ActionFailureHostSource,
): ActionFailureHost | null | undefined {
  return typeof source === "function" ? source() : source;
}

/**
 * Report a failed user-initiated action. Returns the user-facing message.
 * Never throws — a reporting path that can throw turns one failure into two.
 */
export function reportActionFailure(
  hostSource: ActionFailureHostSource,
  options: ActionFailureOptions,
): string {
  const message = options.userMessage ?? describeActionFailure(options.action, options.error);
  // A call the sign-out ended is not a failure to tell the NEXT person about:
  // no toast, no error diagnostic (the stores were reset under it).
  if (isSessionEnded(options.error)) return message;
  let host: ActionFailureHost | null | undefined;
  try {
    host = resolveHost(hostSource);
  } catch {
    host = undefined;
  }
  try {
    host?.diagnostics?.error(
      options.store,
      options.code ?? "view.action_failed",
      `${options.action} failed`,
      { error: options.error, data: options.data },
    );
  } catch {
    // Diagnostics must not break the failure path it is reporting on.
  }
  try {
    if (options.notify !== false) {
      host?.notificationStore?.addErrorNotification(message);
    }
  } catch {
    // Nor must the toast channel.
  }
  return message;
}

/**
 * Bind the helper to one store, so call sites read as
 * `this.reportFailure("remove the meeting source", err)`.
 *
 * In a ViewStore, pass the host as a THUNK — see {@link ActionFailureHostSource}:
 *
 *     private reportFailure = createActionReporter(
 *       () => this.rootStore,
 *       "MeetingSourcesStore",
 *     );
 */
export function createActionReporter(
  host: ActionFailureHostSource,
  storeName: string,
) {
  return (
    action: string,
    error: unknown,
    extra?: Omit<ActionFailureOptions, "store" | "action" | "error">,
  ): string =>
    reportActionFailure(host, { store: storeName, action, error, ...extra });
}

export default reportActionFailure;
